import React, { useState, useEffect } from "react"
import Button from "@mui/material/Button"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import PermissionDlg from "./PermissionDlg"
import { verifyResponse } from "utils/httpRequest"
import { getPermissionModules, getPermissions } from "services/permission"
import { PERMISSIONS } from "constants/permission"
import { useSnackbar } from "notistack"
import { UsePermissionVerification } from "components/customHooks"
import PermissionTable from "./PermissionTable"
import LoadingScreen from "components/common/LoadingScreen"
import { StyledPermission } from "./Permission.style"

const Permission = () => {
  const [permissionLoading, setPermissionLoading] = useState(false)
  const [moduleLoading, setModuleLoading] = useState(false)
  const [permissionModules, setPermissionModules] = useState([])
  const [permissions, setPermissions] = useState([])
  const { enqueueSnackbar } = useSnackbar()

  const AccessControl = UsePermissionVerification()

  const [openPermissionDialog, permissionDialog, permissionDialogLoading] =
    PermissionDlg({
      onSubmit: () => {
        loadPermissions()
      },
      modules: permissionModules,
    })

  const loadPermissionModules = async () => {
    setModuleLoading(true)
    try {
      const response = await getPermissionModules()
      verifyResponse(response)
      setPermissionModules(response.data || [])
    } catch {
      setPermissionModules([])
      enqueueSnackbar("Failed to load permission modules.", {
        variant: "warning",
      })
    } finally {
      setModuleLoading(false)
    }
  }

  const loadPermissions = async () => {
    setPermissionLoading(true)
    try {
      const response = await getPermissions()
      verifyResponse(response)
      setPermissions(response.data.list || [])
    } catch {
      setPermissions([])
      enqueueSnackbar("Failed to load permissions.", {
        variant: "warning",
      })
    } finally {
      setPermissionLoading(false)
    }
  }

  useEffect(() => {
    loadPermissionModules()
    loadPermissions()
  }, [])

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listPermissionAccess: AccessControl.VerifyAccess([
        PERMISSIONS.PERMISSION.PERMISSION_LIST,
      ]),
      addPermissionAccess: AccessControl.VerifyAccess([
        PERMISSIONS.PERMISSION.PERMISSION_ADD,
      ]),
      editPermissionAccess: AccessControl.VerifyAccess([
        PERMISSIONS.PERMISSION.PERMISSION_EDIT,
      ]),
      deletePermissionAccess: AccessControl.VerifyAccess([
        PERMISSIONS.PERMISSION.PERMISSION_DELETE,
      ]),
    })
  }, [AccessControl.UserPermissions])

  return (
    <PageContent>
      <StyledPermission>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">Permissions</h1>
            <p className="page-subtitle">
              Maintain API and feature-level permission entries used by roles.
            </p>
          </div>
          <div className="actions">
            {AccessControl.Access.addPermissionAccess && (
              <Button
                className="new-permission-btn"
                variant="contained"
                size="medium"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => {
                  openPermissionDialog()
                }}
              >
                New Permission
              </Button>
            )}
          </div>
        </PageHeading>
        <div className="page-surface">
          {!AccessControl.Access.listPermissionAccess && (
            <div className="page-empty">
              You do not have permission to view permission records.
            </div>
          )}
          {AccessControl.Access.listPermissionAccess && (
            <PermissionTable
              permissions={permissions}
              access={AccessControl.Access}
              openPermissionDialog={openPermissionDialog}
            />
          )}
        </div>
      </StyledPermission>
      <LoadingScreen
        loading={permissionLoading || moduleLoading || permissionDialogLoading}
      />
      {permissionDialog}
    </PageContent>
  )
}

export default Permission
